import { API_CONFIG, setAuthToken, removeAuthToken, getAuthToken } from '../config/api';
import { getPlatform } from '../config/platform';
import { apiService } from './apiService';
import { ApiError } from './apiService';
import { hasUserRole } from '../utils/userRoles';
import { clearStoredDriverMobileUser, setStoredDriverMobileUser } from '../utils/driverMobileSession';

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  fullName: string;
  phone?: string;
  role?: string;
}

interface DriverRegisterData {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  tcNumber?: string;
  dateOfBirth?: string;
  address?: string;
  cekiciPlaka?: string;
  dorsePlaka?: string;
  licenseNumber?: string;
  licenseClass?: string;
  experienceYears?: number;
  vehicleType?: string;
}

interface AuthResponse {
  success: boolean;
  data?: {
    token: string;
    requiresTwoFactor?: boolean;
    maskedPhone?: string;
    user: {
      id: number;
      username?: string;
      email: string;
      fullName?: string;
      full_name?: string;
      role: string;
      roles?: string[];
      role_ids?: string[];
      phone?: string;
      is_payment_preapproval_controller?: boolean;
      is_payment_final_approver?: boolean;
      isImpersonating?: boolean;
      impersonatedById?: number | string | null;
      impersonatedByName?: string | null;
      originalUserId?: number | string | null;
    };
    driver?: any;
  };
  message?: string;
}

const TOKEN_KEY = 'authToken';
const USER_KEY = 'currentUser';
const DRIVER_KEY = 'currentDriver';
const SESSION_USER_KEY = 'sessionUser';
const IMPERSONATION_SESSION_KEY = 'impersonationSession';

const normalizeCorporateSubUserAsSpotDriver = (user: any) => {
  const isCorporateSubUser = hasUserRole(user, 'corporate-external-transport-user') || (hasUserRole(user, 'corporate-external-transport') && Boolean(user?.externalTransportAccountUserId || user?.external_transport_account_user_id));
  if (!isCorporateSubUser) return user;

  return {
    ...user,
    originalRole: user.originalRole || user.role,
    role: 'external-driver',
    roles: ['external-driver'],
    roleIds: ['external-driver'],
    role_ids: ['external-driver'],
    driverType: 'external',
  };
};

const persistSession = (payload: { token: string; user: any; driver?: any }) => {
  const normalizedUser = normalizeCorporateSubUserAsSpotDriver(payload.driver
    ? {
        ...payload.user,
        driverId: payload.driver.id ?? payload.user?.driverId ?? null,
        driverType: payload.driver.driver_type || payload.driver.type || payload.user?.driverType || null,
      }
    : payload.user);

  setAuthToken(payload.token);
  localStorage.setItem(TOKEN_KEY, payload.token);
  localStorage.setItem('token', payload.token);
  localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
  sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(normalizedUser));

  if (payload.driver) {
    localStorage.setItem(DRIVER_KEY, JSON.stringify(payload.driver));
  } else {
    localStorage.removeItem(DRIVER_KEY);
  }

  if (hasUserRole(normalizedUser, 'internal-driver', 'external-driver', 'corporate-external-transport-driver', 'corporate-external-transport-user') || String(normalizedUser?.driverType || '').length > 0) {
    setStoredDriverMobileUser(normalizedUser);
  } else {
    clearStoredDriverMobileUser();
  }
};

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    if (API_CONFIG.USE_MOCK_API) {
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      const user = users.find((u: any) => u.email === credentials.email);
      
      if (!user || user.password !== credentials.password) {
        return {
          success: false,
          message: 'Email veya şifre hatalı',
        };
      }

      if (user.status !== 'active') {
        return {
          success: false,
          message: 'Hesabınız aktif değil',
        };
      }

      const token = `mock_token_${user.id}_${Date.now()}`;
      localStorage.removeItem(IMPERSONATION_SESSION_KEY);
      setAuthToken(token);
      
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));

      let driverInfo = null;
      if (hasUserRole(user, 'internal-driver', 'external-driver', 'corporate-external-transport-driver', 'corporate-external-transport-user') || user.role.includes('driver')) {
        const drivers = JSON.parse(localStorage.getItem('drivers') || '[]');
        driverInfo = drivers.find((d: any) => d.user_id === user.id);
        if (driverInfo) {
          localStorage.setItem(DRIVER_KEY, JSON.stringify(driverInfo));
        }
      }

      return {
        success: true,
        data: {
          token,
          user: {
            id: user.id,
            username: user.username,
            email: user.email,
            fullName: user.full_name || user.username,
            role: user.role,
            phone: user.phone,
          },
          driver: driverInfo,
        },
      };
    } else {
      try {
        const response = await apiService.post<AuthResponse>('/auth/login', {
          ...credentials,
          platform: getPlatform(),
        });
        
        if (response.success && response.data?.token) {
          const { token, user, driver } = response.data;
          localStorage.removeItem(IMPERSONATION_SESSION_KEY);
          persistSession({ token, user, driver });
        }
        
        return response;
      } catch (error) {
        console.error('Login error:', error);

        if (error instanceof ApiError) {
          return {
            success: false,
            message: error.message || 'Giriş başarısız',
          };
        }

        return {
          success: false,
          message: 'Bağlantı hatası oluştu',
        };
      }
    }
  },

  async verifyLoginCode(email: string, code: string): Promise<AuthResponse> {
    try {
      const response = await apiService.post<AuthResponse>('/auth/verify-login-code', {
        email,
        code,
        platform: getPlatform(),
      });

      if (response.success && response.data?.token) {
        localStorage.removeItem(IMPERSONATION_SESSION_KEY);
        persistSession({
          token: response.data.token,
          user: response.data.user,
          driver: response.data.driver,
        });
      }

      return response;
    } catch (error) {
      console.error('Verify login code error:', error);

      if (error instanceof ApiError) {
        return {
          success: false,
          message: error.message || 'Doğrulama başarısız',
        };
      }

      return {
        success: false,
        message: 'Bağlantı hatası oluştu',
      };
    }
  },

  async forgotPassword(email: string): Promise<{ success: boolean; message?: string }> {
    try {
      return await apiService.post('/auth/forgot-password', { email });
    } catch (error) {
      if (error instanceof ApiError) {
        return { success: false, message: error.message || 'Şifre sıfırlama başarısız' };
      }
      return { success: false, message: 'Bağlantı hatası oluştu' };
    }
  },

  async register(data: RegisterData): Promise<AuthResponse> {
    if (API_CONFIG.USE_MOCK_API) {
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      
      if (users.find((u: any) => u.email === data.email)) {
        return {
          success: false,
          message: 'Bu email zaten kullanılıyor',
        };
      }

      const newUser = {
        id: Date.now(),
        ...data,
        status: 'pending',
        created_at: new Date().toISOString(),
      };

      users.push(newUser);
      localStorage.setItem('users', JSON.stringify(users));

      return {
        success: true,
        message: 'Kayıt başarılı! Onay bekliyor.',
        data: { 
          token: '',
          user: {
            id: newUser.id,
            username: newUser.username,
            email: newUser.email,
            fullName: newUser.full_name,
            role: newUser.role,
          }
        },
      };
    } else {
      try {
        return await apiService.post<AuthResponse>('/auth/register', data);
      } catch (error) {
        console.error('Register error:', error);
        return {
          success: false,
          message: 'Kayıt hatası oluştu',
        };
      }
    }
  },

  async driverRegister(data: DriverRegisterData): Promise<AuthResponse> {
    if (API_CONFIG.USE_MOCK_API) {
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      
      if (users.find((u: any) => u.email === data.email)) {
        return {
          success: false,
          message: 'Bu email zaten kayıtlı',
        };
      }

      const newUser = {
        id: Date.now(),
        username: data.email.split('@')[0],
        email: data.email,
        password: data.password,
        full_name: data.fullName,
        phone: data.phone,
        role: 'external-driver',
        status: 'pending',
        created_at: new Date().toISOString(),
      };

      users.push(newUser);
      localStorage.setItem('users', JSON.stringify(users));

      const applications = JSON.parse(localStorage.getItem('driverApplications') || '[]');
      applications.push({
        id: Date.now(),
        ...data,
        status: 'pending',
        created_at: new Date().toISOString(),
      });
      localStorage.setItem('driverApplications', JSON.stringify(applications));

      return {
        success: true,
        message: 'Başvurunuz alındı! Onay sürecinden sonra bilgilendirileceksiniz.',
        data: {
          token: '',
          user: {
            id: newUser.id,
            email: newUser.email,
            fullName: newUser.full_name,
            role: newUser.role,
          }
        },
      };
    } else {
      try {
        return await apiService.post<AuthResponse>('/auth/driver-register', data);
      } catch (error) {
        console.error('Driver register error:', error);
        return {
          success: false,
          message: 'Başvuru hatası oluştu',
        };
      }
    }
  },

  async getCurrentUser(): Promise<{ success: boolean; data?: any; message?: string }> {
    const token = getAuthToken();
    
    if (!token) {
      return { success: false };
    }

    if (API_CONFIG.USE_MOCK_API) {
      const user = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
      return {
        success: !!user,
        data: user,
      };
    } else {
      try {
        return await apiService.get('/auth/me');
      } catch (error) {
        console.error('Get current user error:', error);
        return { success: false, message: 'Kullanıcı bilgileri alınamadı' };
      }
    }
  },

  async updateProfile(userId: number, data: any): Promise<AuthResponse> {
    if (API_CONFIG.USE_MOCK_API) {
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      const index = users.findIndex((u: any) => u.id === userId);
      
      if (index === -1) {
        return { success: false, message: 'Kullanıcı bulunamadı' };
      }

      users[index] = { ...users[index], ...data };
      localStorage.setItem('users', JSON.stringify(users));
      localStorage.setItem(USER_KEY, JSON.stringify(users[index]));

      return {
        success: true,
        data: {
          token: getAuthToken() || '',
          user: users[index],
        },
      };
    } else {
      try {
        return await apiService.put<AuthResponse>(`/users/${userId}`, data);
      } catch (error) {
        console.error('Update profile error:', error);
        return { success: false, message: 'Profil güncellenemedi' };
      }
    }
  },

  logout() {
    const token = getAuthToken();
    if (!API_CONFIG.USE_MOCK_API && token) {
      fetch(`${API_CONFIG.BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ platform: getPlatform() }),
        keepalive: true,
      }).catch((error) => {
        console.error('Logout audit request failed:', error);
      });
    }

    removeAuthToken();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('token');
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(DRIVER_KEY);
    localStorage.removeItem(IMPERSONATION_SESSION_KEY);
    sessionStorage.removeItem(SESSION_USER_KEY);
    clearStoredDriverMobileUser();
  },

  async impersonate(userId: string | number): Promise<AuthResponse> {
    try {
      const currentToken = getAuthToken();
      const currentUser = this.getStoredUser();
      const currentDriver = this.getStoredDriver();

      if (!currentToken || !currentUser) {
        return { success: false, message: 'Aktif oturum bulunamadı' };
      }

      if (!localStorage.getItem(IMPERSONATION_SESSION_KEY)) {
        localStorage.setItem(IMPERSONATION_SESSION_KEY, JSON.stringify({
          token: currentToken,
          user: currentUser,
          driver: currentDriver,
        }));
      }

      const response = await apiService.post<AuthResponse>(`/auth/impersonate/${userId}`, { platform: getPlatform() });
      if (response.success && response.data?.token) {
        persistSession({
          token: response.data.token,
          user: response.data.user,
          driver: response.data.driver,
        });
      }
      return response;
    } catch (error) {
      console.error('Impersonation error:', error);
      return {
        success: false,
        message: error instanceof ApiError ? error.message : 'Bu kullanıcı ile giriş yapılamadı',
      };
    }
  },

  restoreOriginalSession() {
    try {
      const raw = localStorage.getItem(IMPERSONATION_SESSION_KEY);
      if (!raw) return null;

      const original = JSON.parse(raw);
      if (!original?.token || !original?.user) return null;

      persistSession({
        token: original.token,
        user: original.user,
        driver: original.driver,
      });
      localStorage.removeItem(IMPERSONATION_SESSION_KEY);
      return original;
    } catch (error) {
      console.error('Restore original session error:', error);
      return null;
    }
  },

  getImpersonationSession() {
    try {
      const raw = localStorage.getItem(IMPERSONATION_SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },

  getStoredUser() {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  getStoredDriver() {
    const driver = localStorage.getItem(DRIVER_KEY);
    return driver ? JSON.parse(driver) : null;
  },

  isAuthenticated(): boolean {
    return !!getAuthToken();
  },

  getToken(): string | null {
    return getAuthToken();
  },

  async deleteMyAccount() {
    return await apiService.delete('/auth/me');
  },

  async generateAdminVerificationCode(userId?: string | number, applicationId?: string | number) {
    return await apiService.post<{ data: { code: string; phone: string; fullName: string; expiresAt: number } }>('/auth/admin-generate-verification-code', { userId, applicationId });
  },
};
